import { INTERNAL_DOMAINS_LABEL, isInternalEmail } from '../../data/recipients';
import { easternIsoDay } from '../../data/taskRules';
import { Modal } from '../../ui/Modal';
import { PersonName } from '../../ui/PersonName';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { AuditForm } from '../../ui/audit/AuditForm';
import { useAppUser } from '../../auth/AppUserContext';
import { canSeeScoreBeforeSubmit, isAdmin } from '../../data/scope';
import { scoreAudit } from '../../audit/scoring';
import { answerProgress, answeredCount } from '../../audit/answers';
import {
  allowedKinds,
  attachmentPolicy,
  isAttachmentRequired,
  missingRequiredAttachments,
  MAX_PDF_BYTES,
  type AttachmentKind,
} from '../../audit/attachmentRules';
import { compressPhoto } from '../../local/imageCompression';
import { fillRandomAnswers } from '../../audit/testFill';
import { sessionStore, getSyncEngine } from '../../local';
import type { LocalAttachment, LocalAuditSession, SyncStatus } from '../../local/types';
import { definitionHasScore, type AnswerMap, type Question } from '../../audit/template';
import { failedQuestions } from '../../audit/findings';
import { PrincipalField } from '../../ui/PrincipalField';
import { ImageLightbox } from '../../ui/ImageLightbox';
import { cancelLocalAudit, checkStillOwned, kpiContextStale, loadKpiContext } from '../../audit/lifecycle';
import { KpiHintLine } from '../../ui/audit/KpiHint';
import {
  AUDIT_EXPIRY_HOURS,
  EXPIRY_WARNING_HOURS,
  hoursUntilExpiry,
  isAuditExpired,
} from '../../audit/expiry';
import { ExpiryNotice } from '../../ui/audits/ExpiryNotice';
import { downloadAuditAttachment } from '../../data/audits';
import { CancelAuditDialog } from '../../ui/audits/CancelAuditDialog';
import { closedAuditMessage, pendingAdditive } from '../../ui/audits/closedAudit';
import { isoDaysAgo, formatDay, formatDateTime } from '../../ui/dates';
import { ownsSession } from '../../local/ownership';
import { ActionButton, armTapShield, useActionLock } from '../../ui/ActionButton';
import { scorePct } from '../../ui/scoreText';

type Phase = 'conduct' | 'review' | 'submitted';

/** Sentinel for a general (audit-level) note/task, distinct from "closed". */
const GENERAL = '__general__';

/** What a note/task/upload is being attached to. */
interface ItemTarget {
  questionId: string;
  /** Set when targeting one sample-group entry. */
  sampleIndex?: number;
}

const sameTarget = (
  t: ItemTarget | undefined,
  questionId: string,
  sampleIndex?: number,
): boolean =>
  t !== undefined && t.questionId === questionId && t.sampleIndex === sampleIndex;

/**
 * Conduct an audit against a local session. Answering, notes, and
 * quick-created tasks are all local-first (spec §§6+8): they persist to
 * IndexedDB immediately and sync in the background. Submit flow honors the
 * score-visibility ruling.
 */
export function ConductAuditPage() {
  const { localId } = useParams<{ localId: string }>();
  const { user, realUser } = useAppUser();
  // Shared devices: only the signed-in account's own saved audits open here.
  const signedInUpn = (realUser ?? user)?.upn ?? null;
  const [session, setSession] = useState<LocalAuditSession | null | 'missing'>(null);
  const [phase, setPhase] = useState<Phase>('conduct');
  const [noteFor, setNoteFor] = useState<ItemTarget | undefined>(undefined);
  const [taskFor, setTaskFor] = useState<ItemTarget | undefined>(undefined);
  const [attachError, setAttachError] = useState<string | null>(null);
  const attachTarget = useRef<
    (ItemTarget & { kinds: AttachmentKind[] }) | null
  >(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  /** One file saved at a time: a second pick (or tap) while one saves is refused. */
  const uploadLock = useActionLock();
  /** Confirm & submit / Back to form. */
  const submitLock = useActionLock();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // A note / task / submit that failed on this device (before: an unhandled
  // rejection — the button re-enabled with no word why).
  const [actionError, setActionError] = useState<string | null>(null);
  const reportFailure = (what: string) => (err: unknown) =>
    setActionError(`Couldn’t ${what} (${err instanceof Error ? err.message : String(err)}). Try again.`);
  /**
   * Answer saves not yet committed. The answers on screen are AHEAD of any
   * save still pending, so a store result landing mid-burst must not roll
   * them back (the next keystroke would be built on the stale map). Other
   * mutations (notes, tasks, files, submit/cancel) never change answers, so
   * their results — which can resolve late, e.g. after a blob delete —
   * always keep the on-screen answers.
   */
  const answerSavesInFlight = useRef(0);
  /** Bumped by every applied local save (the sync re-read below compares it). */
  const savesApplied = useRef(0);
  const applySaved = (saved: LocalAuditSession, fromAnswerSave = false) => {
    savesApplied.current += 1;
    setSession((prev) =>
      (!fromAnswerSave || answerSavesInFlight.current > 0) &&
      prev &&
      prev !== 'missing'
        ? { ...saved, answers: prev.answers }
        : saved,
    );
  };
  /**
   * Questions whose save FAILED. The next save writes the whole answers map
   * (so their values land) and must mark them dirty too, or they'd be
   * stored but never uploaded.
   */
  const failedSaveIds = useRef(new Set<string>());
  const persistAnswers = (next: AnswerMap, changed: string | string[]) => {
    if (!session || session === 'missing') return;
    setSession((prev) => (prev && prev !== 'missing' ? { ...prev, answers: next } : prev));
    const ids = [
      ...new Set([
        ...(Array.isArray(changed) ? changed : [changed]),
        ...failedSaveIds.current,
      ]),
    ];
    answerSavesInFlight.current += 1;
    sessionStore.saveAnswers(session.localId, next, ids).then(
      (saved) => {
        answerSavesInFlight.current -= 1;
        for (const id of ids) failedSaveIds.current.delete(id);
        if (failedSaveIds.current.size === 0) setSaveError(null);
        applySaved(saved, true);
        getSyncEngine().kick();
      },
      (err: unknown) => {
        answerSavesInFlight.current -= 1;
        for (const id of ids) failedSaveIds.current.add(id);
        setSaveError(err instanceof Error ? err.message : String(err));
      },
    );
  };

  useEffect(() => {
    if (!localId || !signedInUpn) return;
    let stale = false;
    void (async () => {
      let s = await sessionStore.getSession(localId);
      // Another account's audit saved on this shared device: not ours to open.
      if (s && !ownsSession(s, signedInUpn)) s = undefined;
      // Past its 72 hours? Close it before anyone can keep editing.
      if (s?.status === 'in_progress' && isAuditExpired(s.startedAt)) {
        s = await sessionStore.closeSession(s.localId, 'expired');
        getSyncEngine().kick();
      }
      if (stale) return;
      setSession(s ?? 'missing');
      if (s?.status === 'submitted') setPhase('submitted');
      // Online: has another device taken this audit over or closed it?
      if (s?.status === 'in_progress' && s.serverId !== null) {
        const checked = await checkStillOwned(s);
        if (!stale && checked.status !== s.status) setSession(checked);
      }
    })();
    return () => {
      stale = true;
    };
  }, [localId, signedInUpn]);

  const loaded = session !== null && session !== 'missing';
  // KPI hints (B8): an audit started before them, or whose snapshot is older
  // than 12 hours, re-reads the store's last values and budgets when online.
  // Once per page; a failure just leaves the hints as they are.
  const kpiReadFor = useRef<string | null>(null);
  // A page opened with no signal reads them once the signal comes back (QA 2026-10-25).
  const [onlineTick, setOnlineTick] = useState(0);
  useEffect(() => {
    const back = () => setOnlineTick((n) => n + 1);
    window.addEventListener('online', back);
    return () => window.removeEventListener('online', back);
  }, []);
  useEffect(() => {
    if (!session || session === 'missing' || session.status !== 'in_progress') return;
    if (kpiReadFor.current === session.localId) return;
    if (!kpiContextStale(session)) return;
    // Offline: try again on 'online' (the guard is only set once a read is tried).
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    kpiReadFor.current = session.localId;
    void loadKpiContext(session)
      .then((saved) => {
        const kpiContext = saved?.kpiContext;
        if (!saved || !kpiContext) return;
        setSession((prev) =>
          prev && prev !== 'missing' && prev.localId === saved.localId ? { ...prev, kpiContext } : prev,
        );
      })
      .catch(() => undefined);
  }, [loaded, localId, onlineTick]); // once per loaded audit (retried when signal returns)

  // The sync engine writes upload results (server ids, uploaded files,
  // cleared dirty flags) to the device, not to this page: re-read the saved
  // copy whenever its status changes, so "N items will upload", file chips
  // and the "View audit record" link follow it (2026-10-19 DM exploration:
  // the submitted card said "75 items will upload" long after they had).
  // On-screen answers always win, and a re-read that a local save
  // overtook is dropped, so this can never undo an edit. Nothing changes on
  // screen unless something upload-related did (no re-render of the whole
  // form on every idle tick), and the template object is kept (memos hold).
  const [syncState, setSyncState] = useState<SyncStatus['state']>('synced');
  useEffect(() => {
    if (!localId || !signedInUpn || !loaded) return;
    let stale = false;
    const unsubscribe = getSyncEngine().subscribe((st) => {
      setSyncState(st.state);
      const seq = savesApplied.current;
      void sessionStore.getSession(localId).then((s) => {
        if (stale || !s || !ownsSession(s, signedInUpn) || seq !== savesApplied.current) return;
        setSession((prev) =>
          !prev || prev === 'missing' || prev.localId !== s.localId
            ? prev
            : syncSignature(prev) === syncSignature(s)
              ? prev
              : {
                  ...s,
                  // Still editable: the screen's answers are ahead. Closed
                  // (e.g. submitted in another tab): the stored ones are final.
                  answers: s.status === 'in_progress' ? prev.answers : s.answers,
                  definition: prev.definition,
                  settings: prev.settings,
                },
        );
        // Submitted from another tab on this device.
        if (s.status === 'submitted') setPhase('submitted');
      });
    });
    return () => {
      stale = true;
      unsubscribe();
    };
  }, [localId, signedInUpn, loaded]);

  const score = useMemo(
    () =>
      session && session !== 'missing'
        ? scoreAudit(session.definition, session.answers, session.settings)
        : null,
    [session],
  );

  if (!localId || session === 'missing') {
    return (
      <div className="page-message">
        <p>This audit isn’t on this device for your account.</p>
        <Link to="/audits">Back to audits</Link>
      </div>
    );
  }
  if (session === null || !user) {
    return <div className="page-message">Loading audit…</div>;
  }

  // Cancelled, expired, or closed by another device: no more editing here.
  if (
    session.status === 'cancelled' ||
    session.status === 'expired' ||
    session.status === 'closed_elsewhere'
  ) {
    const uploading =
      session.status === 'closed_elsewhere' ? pendingAdditive(session) : 0;
    return (
      <div className="page-message">
        <h1>
          {session.storeNumber} — {session.storeName}
        </h1>
        <p>{closedAuditMessage(session)}</p>
        {session.status !== 'closed_elsewhere' && (
          <p className="muted">
            It stays on record and does not count as a completed
            audit. Tasks created during it stay open.
          </p>
        )}
        {uploading > 0 && (
          <p className="muted">
            {uploading} note/task/photo item{uploading > 1 ? 's' : ''} from
            this device will still upload to the audit record.
          </p>
        )}
        <Link className="btn btn-primary" to="/audits">
          Back to audits
        </Link>
      </div>
    );
  }

  const onAnswersChange = (next: AnswerMap, changedQuestionId: string) =>
    persistAnswers(next, changedQuestionId);

  const saveNote = (target: ItemTarget, text: string) =>
    saveNoteNow(target, text).catch(reportFailure('save the note on this device'));
  const saveNoteNow = async (target: ItemTarget, text: string) => {
    setActionError(null);
    const saved = await sessionStore.addNote(session.localId, {
      questionId: target.questionId === GENERAL ? null : target.questionId,
      ...(target.sampleIndex !== undefined
        ? { sampleIndex: target.sampleIndex }
        : {}),
      text,
      author: user.upn,
    });
    applySaved(saved);
    setNoteFor(undefined);
    getSyncEngine().kick();
  };

  type TaskInput = { title: string; description: string; assignedTo: string; dueDate: string; proofRequired?: boolean };
  const saveTask = (target: ItemTarget, task: TaskInput) =>
    saveTaskNow(target, task).catch(reportFailure('save the task on this device'));
  const saveTaskNow = async (target: ItemTarget, task: TaskInput) => {
    setActionError(null);
    const saved = await sessionStore.addTaskDraft(session.localId, {
      questionId: target.questionId === GENERAL ? null : target.questionId,
      ...(target.sampleIndex !== undefined
        ? { sampleIndex: target.sampleIndex }
        : {}),
      ...task,
      createdBy: user.upn,
    });
    applySaved(saved);
    setTaskFor(undefined);
    getSyncEngine().kick();
  };

  const confirmSubmit = () => confirmSubmitNow().catch(reportFailure('submit the audit on this device'));
  const confirmSubmitNow = async () => {
    if (!score) return;
    setActionError(null);
    // The 72 hours are measured on this device at the moment of submit, so
    // an audit submitted offline in time still counts when it syncs later.
    if (isAuditExpired(session.startedAt)) {
      applySaved(await sessionStore.closeSession(session.localId, 'expired'));
      getSyncEngine().kick();
      return;
    }
    // A change that failed to save would be counted in the score but never
    // reach SharePoint (submitted audits take no more answer saves).
    if (failedSaveIds.current.size > 0) {
      setSaveError(
        'so the audit was NOT submitted',
      );
      setPhase('conduct');
      return;
    }
    // Score what is STORED: IndexedDB orders this read after every save
    // already issued, and it includes answers another tab saved.
    const stored = await sessionStore.getSession(session.localId);
    if (!stored) return;
    const saved = await sessionStore.markSubmitted(
      session.localId,
      scoreAudit(stored.definition, stored.answers, stored.settings),
    );
    applySaved(saved, true);
    if (saved.status === 'submitted') setPhase('submitted');
    getSyncEngine().kick(0);
  };

  const findQuestion = (questionId: string): Question | undefined =>
    session.definition.sections
      .flatMap((s) => s.questions)
      .find((q) => q.id === questionId);

  const questionText = (target: ItemTarget): string => {
    if (target.questionId === GENERAL) return `General — ${session.templateName}`;
    const base = findQuestion(target.questionId)?.text ?? target.questionId;
    return target.sampleIndex !== undefined
      ? `${base} — entry ${target.sampleIndex + 1}`
      : base;
  };

  const startAttach = (target: ItemTarget, kinds: AttachmentKind[]) => {
    if (uploadLock.locked) return;
    attachTarget.current = { ...target, kinds };
    const input = fileInputRef.current;
    if (!input) return;
    // One picker for both kinds: the accept list narrows to the question's
    // config. Camera-direct capture only when photos are the ONLY option —
    // the capture attribute would otherwise hide the file browser on phones.
    input.accept = [
      ...(kinds.includes('photo') ? ['image/*'] : []),
      ...(kinds.includes('pdf') ? ['application/pdf'] : []),
    ].join(',');
    if (kinds.length === 1 && kinds[0] === 'photo') {
      input.setAttribute('capture', 'environment');
    } else {
      input.removeAttribute('capture');
    }
    input.click();
  };

  const onFilePicked = async (file: File | undefined) => {
    const target = attachTarget.current;
    if (!file || !target) return;
    setAttachError(null);
    try {
      const isPdf =
        file.type === 'application/pdf' ||
        file.name.toLowerCase().endsWith('.pdf');
      const kind: AttachmentKind = isPdf ? 'pdf' : 'photo';
      if (!target.kinds.includes(kind)) {
        throw new Error(
          kind === 'pdf'
            ? 'This question only accepts photos.'
            : 'This question only accepts PDF documents.',
        );
      }
      let blob: Blob = file;
      let mimeType = file.type || 'application/octet-stream';
      if (kind === 'photo') {
        blob = await compressPhoto(file);
        mimeType = 'image/jpeg';
      } else if (file.size > MAX_PDF_BYTES) {
        throw new Error(
          `That PDF is ${(file.size / 1024 / 1024).toFixed(1)} MB — the limit is ${MAX_PDF_BYTES / 1024 / 1024} MB.`,
        );
      }
      const saved = await sessionStore.addAttachment(
        session.localId,
        {
          questionId: target.questionId,
          ...(target.sampleIndex !== undefined
            ? { sampleIndex: target.sampleIndex }
            : {}),
          kind,
          mimeType,
          uploadedBy: user.upn,
        },
        blob,
      );
      applySaved(saved);
      getSyncEngine().kick();
    } catch (err) {
      setAttachError(err instanceof Error ? err.message : String(err));
    }
  };

  const removeAttachment = async (attachmentId: string) => {
    try {
      const saved = await sessionStore.removeAttachment(session.localId, attachmentId);
      applySaved(saved);
    } catch (err) {
      setAttachError(err instanceof Error ? err.message : String(err));
    }
  };

  /** Badge buttons anchored right of the question text (spec §6). */
  const questionActions = (questionId: string, sampleIndex?: number): ReactNode => {
    if (phase !== 'conduct') return null;
    const question = findQuestion(questionId);
    // Sample-group level gets note/task only; upload lives on each entry.
    const kinds =
      question &&
      attachmentPolicy(question) !== 'none' &&
      !(question.type === 'sample_group' && sampleIndex === undefined)
        ? allowedKinds(question)
        : [];
    const target: ItemTarget = {
      questionId,
      ...(sampleIndex !== undefined ? { sampleIndex } : {}),
    };
    const what = sampleIndex !== undefined ? 'sample' : 'question';
    return (
      <>
        <button
          type="button"
          className="badge-btn badge-note"
          title={`Add a note to this ${what}`}
          onClick={() => setNoteFor(target)}
        >
          + Note
        </button>
        {/* Store teams note and photograph their self-audits; tasks are raised by DMs/Auditors. */}
        {user?.role !== 'StoreTeam' && (
          <button
            type="button"
            className="badge-btn badge-task"
            title={`Create a task from this ${what}`}
            onClick={() => setTaskFor(target)}
          >
            + Task
          </button>
        )}
        {kinds.length > 0 && (
          <button
            type="button"
            className="badge-btn badge-photo"
            title={
              kinds.length === 2
                ? 'Attach a photo or PDF to this question'
                : kinds[0] === 'pdf'
                  ? 'Attach a PDF to this question'
                  : 'Attach a photo to this question'
            }
            disabled={uploadLock.busy}
            onClick={() => startAttach(target, kinds)}
          >
            {uploadLock.busy && sameTarget(attachTarget.current ?? undefined, questionId, sampleIndex)
              ? 'Uploading…'
              : '+ Upload'}
          </button>
        )}
      </>
    );
  };

  /** Saved notes/tasks/attachments and the inline note editor. */
  const questionExtras = (questionId: string, sampleIndex?: number): ReactNode => {
    if (phase !== 'conduct') return null;
    const notes = (session.notes ?? []).filter(
      (n) => n.questionId === questionId && n.sampleIndex === sampleIndex,
    );
    const tasks = (session.taskDrafts ?? []).filter(
      (t) => t.questionId === questionId && t.sampleIndex === sampleIndex,
    );
    const attachments = (session.attachments ?? []).filter(
      (a) => a.questionId === questionId && a.sampleIndex === sampleIndex,
    );
    const question = findQuestion(questionId);
    // The requirement hint sits at question level; ANY attachment on the
    // question (incl. per-sample ones) satisfies it.
    const needsAttachment =
      sampleIndex === undefined &&
      question !== undefined &&
      !(session.attachments ?? []).some((a) => a.questionId === questionId) &&
      isAttachmentRequired(question, session.answers[questionId]);
    const editorOpen = sameTarget(noteFor, questionId, sampleIndex);
    if (
      notes.length === 0 &&
      tasks.length === 0 &&
      attachments.length === 0 &&
      !needsAttachment &&
      !editorOpen
    ) {
      return null;
    }
    return (
      <div className="q-extras">
        {needsAttachment && (
          <div className="q-attach-required">
            📷 A photo (or PDF) is required for this answer — use “+ Upload”
            above.
          </div>
        )}
        {attachments.length > 0 && (
          <div className="q-attachments">
            {attachments.map((a) => (
              <AttachmentChip
                key={a.id}
                attachment={a}
                onRemove={() => removeAttachment(a.id)}
              />
            ))}
          </div>
        )}
        {notes.map((n) => (
          <div className="q-note" key={n.id}>
            🗒 {n.text}
            <span className="muted"> — <PersonName upn={n.author} /></span>
            {n.rejected && <span className="error"> · not saved — {n.rejected}</span>}
          </div>
        ))}
        {tasks.map((t) => (
          <div className="q-note q-note-task" key={t.id}>
            ☑ Task: {t.title}
            <span className="muted">
              {' '}
              → <PersonName upn={t.assignedTo} />, due {formatDay(t.dueDate) || '—'}
            </span>
            {t.rejected && <span className="error"> · not created — {t.rejected}</span>}
          </div>
        ))}
        {editorOpen && (
          <NoteEditor
            onSave={(text) =>
              saveNote(
                {
                  questionId,
                  ...(sampleIndex !== undefined ? { sampleIndex } : {}),
                },
                text,
              )
            }
            onCancel={() => {
              setActionError(null);
              setNoteFor(undefined);
            }}
            error={actionError}
          />
        )}
      </div>
    );
  };

  const showScoreNow = phase === 'submitted' || canSeeScoreBeforeSubmit(user.role);
  const progress = answerProgress(session.definition, session.answers);
  const answered = answeredCount(session.definition, session.answers);
  const pendingSync =
    session.dirtyQuestions.length +
    (session.headerDirty ? 1 : 0) +
    (session.notes ?? []).filter((n) => n.serverId === undefined && !n.rejected).length +
    (session.taskDrafts ?? []).filter((t) => t.serverId === undefined && !t.rejected).length +
    (session.attachments ?? []).filter(
      (a) => a.serverItemId === undefined && !a.missingBlob,
    ).length;

  const onFormSubmit = () => {
    const missing = missingRequiredAttachments(
      session.definition,
      session.answers,
      // A file the server refused doesn't count — the user re-attaches it.
      (session.attachments ?? []).filter((a) => !a.rejected),
    );
    if (missing.length > 0) {
      setAttachError(
        `${missing.length} question${missing.length > 1 ? 's' : ''} still need${missing.length > 1 ? '' : 's'} a required photo: ${missing
          .slice(0, 3)
          .map((q) => `“${q.text.slice(0, 60)}”`)
          .join(', ')}${missing.length > 3 ? '…' : ''}`,
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setAttachError(null);
    setPhase('review');
    // Start the review at its top: a double tap on the form's Submit must
    // not land its second tap on "Confirm & submit" where Submit was.
    window.scrollTo({ top: 0 });
  };

  return (
    <div className="audit-page">
      <div className="page-head">
        <div>
          <h1>
            {session.storeNumber} — {session.storeName}
          </h1>
          <p className="muted" style={{ margin: 0 }}>
            {session.templateName} · started {formatDateTime(session.startedAt)}
          </p>
        </div>
        <div className="page-head-tools">
          {phase === 'conduct' && (
            <>
              <button
                className="btn"
                onClick={() => setNoteFor({ questionId: GENERAL })}
              >
                + General note
              </button>
              {isAdmin(user.role) && (
                <button
                  className="btn"
                  title="Testing: fill every remaining visible question with plausible random answers"
                  onClick={() => {
                    const filled = fillRandomAnswers(session.definition, session.answers);
                    const changed = Object.keys(filled).filter(
                      (k) => filled[k] !== session.answers[k],
                    );
                    persistAnswers(filled, changed);
                  }}
                >
                  🎲 Fill remaining (test)
                </button>
              )}
              <button className="btn btn-danger" onClick={() => setConfirmCancel(true)}>
                Cancel audit
              </button>
            </>
          )}
          <Link className="btn" to="/audits">
            All audits
          </Link>
        </div>
      </div>

      {noteFor?.questionId === GENERAL && (
        <div className="audit-section">
          <div className="audit-section-body">
            <h3 style={{ marginTop: 0 }}>General audit note</h3>
            <NoteEditor
              onSave={(text) => saveNote({ questionId: GENERAL }, text)}
              onCancel={() => {
              setActionError(null);
              setNoteFor(undefined);
            }}
            error={actionError}
            />
          </div>
        </div>
      )}

      {phase !== 'submitted' &&
        (hoursUntilExpiry(session.startedAt) ?? Infinity) < EXPIRY_WARNING_HOURS && (
          <div className="attr-warning">
            ⏳ Less than {EXPIRY_WARNING_HOURS} hours left. Audits must be
            submitted within {AUDIT_EXPIRY_HOURS} hours of being started —
            after that this one closes and can’t be submitted.
          </div>
        )}

      {phase === 'conduct' &&
        (session.missingAttributes?.length ?? 0) > 0 && (
          <div className="attr-warning">
            ⚠ Store facility data is incomplete (
            {session
              .missingAttributes!.map((a) =>
                a === 'hasParking'
                  ? 'parking'
                  : a === 'hasElevators'
                    ? 'elevators'
                    : a,
              )
              .join(', ')}
            ), so those gate questions were not pre-answered. Answer them
            manually below
            {user.role === 'Admin' ? (
              <>
                , or <Link to="/admin/stores">update the store record</Link>{' '}
                so future audits pre-fill correctly.
              </>
            ) : (
              <> — and ask an admin to complete the store record.</>
            )}
          </div>
        )}

      {/* Note/task failures show inside their editor; this is for submit. */}
      {actionError && phase !== 'submitted' && !noteFor && !taskFor && (
        <div className="attr-warning" role="alert">⚠ {actionError}</div>
      )}

      {saveError && phase === 'conduct' && (
        <div className="attr-warning">
          ⚠ Couldn’t save your last change on this device ({saveError}).
          Reload the page to see what was kept before continuing.
        </div>
      )}

      {attachError && phase === 'conduct' && (
        <div className="attr-warning">⚠ {attachError}</div>
      )}

      {phase === 'conduct' && (
        <AuditForm
          definition={session.definition}
          answers={session.answers}
          onAnswersChange={onAnswersChange}
          onSubmit={onFormSubmit}
          questionExtras={questionExtras}
          questionActions={questionActions}
          questionHint={
            session.kpiContext
              ? (q, value) => <KpiHintLine context={session.kpiContext} question={q} value={value} />
              : undefined
          }
          statusNotice={<ExpiryNotice startedAt={session.startedAt} variant="panel" />}
          onEntryRemoved={(questionId, index) => {
            void sessionStore
              .remapSampleItems(session.localId, questionId, index)
              .then(applySaved);
          }}
        />
      )}

      {/* Hidden picker backing the "+ Upload" badge (accept set per question). */}
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          void uploadLock.run(() => onFilePicked(file));
        }}
      />

      {phase === 'review' && (
        <div className="audit-section">
          <div className="audit-section-body">
            <h2>Ready to submit</h2>
            <p>
              {plural(answered, 'answer')} for {session.storeName}
              {progress.total > 0 && ` · ${progress.answered} of ${progress.total} required`}.
              {(session.notes?.length ?? 0) > 0 &&
                ` ${session.notes!.length} note${session.notes!.length > 1 ? 's' : ''}.`}
              {(session.taskDrafts?.length ?? 0) > 0 &&
                ` ${session.taskDrafts!.length} task${session.taskDrafts!.length > 1 ? 's' : ''} will be assigned.`}
              {(session.attachments?.length ?? 0) > 0 &&
                ` ${session.attachments!.length} attachment${session.attachments!.length > 1 ? 's' : ''} will upload with the audit.`}
            </p>
            {/* The failed answers, so a slip shows before it's locked in (UI review 3, U182). */}
            {(() => {
              const failed = session.definition ? failedQuestions(session.definition, session.answers) : [];
              if (failed.length === 0) return null;
              return (
                <div className="review-failed">
                  <p style={{ margin: '0 0 0.3rem' }}>
                    <strong>{plural(failed.length, 'failed answer')}</strong>
                  </p>
                  <ul>
                    {failed.slice(0, 10).map((q) => (
                      <li key={q.id}>{q.text}</li>
                    ))}
                    {failed.length > 10 && <li className="muted">and {failed.length - 10} more — see the form</li>}
                  </ul>
                </div>
              );
            })()}
            {session.definition && !definitionHasScore(session.definition, session.settings.scoringMode) ? (
              <p className="muted">This form has no score.</p>
            ) : showScoreNow && score ? (
              <p>
                Score:{' '}
                <strong>
                  {score.score === null ? 'n/a' : scorePct(score.score)}
                </strong>
                {score.criticalFailed && (
                  <span className="error"> — critical failure</span>
                )}
              </p>
            ) : (
              <p className="muted">
                The score is calculated at submission and will appear on the
                audit record afterward.
              </p>
            )}
            <p className="muted">
              Submitting locks the audit — its answers can’t be changed
              afterward. If you’re offline, everything (notes, tasks and
              photos too) uploads when you’re back online.
            </p>
            <div className="modal-actions" style={{ justifyContent: 'flex-start' }}>
              <button className="btn" disabled={submitLock.busy} onClick={() => setPhase('conduct')}>
                Back to form
              </button>
              <ActionButton
                className="btn btn-primary"
                lock={submitLock}
                busyLabel="Submitting…"
                onAction={confirmSubmit}
              >
                Confirm &amp; submit
              </ActionButton>
            </div>
          </div>
        </div>
      )}

      {phase === 'submitted' && (
        <div className="audit-section">
          <div className="audit-section-body">
            <h2>Audit submitted</h2>
            {pendingSync > 0 && (
              <p className="muted">
                Saved on this device — {pendingSync} item
                {pendingSync > 1 ? 's' : ''}{' '}
                {!navigator.onLine || syncState === 'offline'
                  ? 'will upload automatically when you’re back online.'
                  : syncState === 'error'
                    ? 'waiting to upload — the app keeps retrying on its own.'
                    : 'uploading now…'}
              </p>
            )}
            {score && definitionHasScore(session.definition, session.settings.scoringMode) && (
              <>
                <p>
                  Final score:{' '}
                  <strong>
                    {score.score === null ? 'n/a' : scorePct(score.score)}
                  </strong>
                  {score.criticalFailed && (
                    <span className="error"> — critical failure</span>
                  )}
                </p>
                <ul>
                  {Object.entries(score.sectionScores).map(([id, s]) => (
                    <li key={id}>
                      {session.definition.sections.find((x) => x.id === id)
                        ?.title ?? id}
                      : {s === null ? 'not applicable' : scorePct(s)}
                    </li>
                  ))}
                </ul>
              </>
            )}
            <div className="modal-actions" style={{ justifyContent: 'flex-start' }}>
              {session.serverId !== null && pendingSync === 0 && (
                <Link
                  className="btn btn-primary"
                  to={`/audits/records/${session.serverId}`}
                >
                  View audit record
                </Link>
              )}
              <Link className="btn" to="/audits">
                Back to audits
              </Link>
            </div>
          </div>
        </div>
      )}

      {confirmCancel && (
        <CancelAuditDialog
          storeLabel={`${session.storeNumber} — ${session.storeName}`}
          onConfirm={async () => {
            applySaved(await cancelLocalAudit(session.localId));
            setConfirmCancel(false);
          }}
          onClose={() => setConfirmCancel(false)}
        />
      )}

      {taskFor !== undefined && (
        <TaskSheet
          contextText={questionText(taskFor)}
          storeLabel={`${session.storeNumber} — ${session.storeName}`}
          templateName={session.templateName}
          defaultAssignee={session.storeAccountUpn ?? ''}
          onSave={(task) => saveTask(taskFor, task)}
          onCancel={() => {
            setActionError(null);
            setTaskFor(undefined);
          }}
          error={actionError}
        />
      )}
    </div>
  );
}

/**
 * One queued/uploaded attachment: thumbnail (photos), status, remove.
 * Clicking previews it — photos open a full-screen overlay, PDFs open in
 * a new tab (the browser's own viewer) — so the auditor can verify they
 * picked the right file.
 */
function AttachmentChip({
  attachment,
  onRemove,
}: {
  attachment: LocalAttachment;
  onRemove: () => Promise<void>;
}) {
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (attachment.kind !== 'photo' || attachment.missingBlob) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    void attachmentBytes(attachment).then((blob) => {
      if (blob && !cancelled) {
        objectUrl = URL.createObjectURL(blob);
        setThumbUrl(objectUrl);
      }
    });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.id, attachment.kind, attachment.missingBlob]);

  const openPreview = () => {
    if (attachment.missingBlob) return;
    if (attachment.kind === 'photo') {
      if (thumbUrl) setPreviewOpen(true);
      return;
    }
    // PDF: hand it to the browser's viewer in a new tab. The URL is revoked
    // after a grace period — revoking immediately can abort the load.
    void attachmentBytes(attachment).then((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(
        blob.type ? blob : new Blob([blob], { type: 'application/pdf' }),
      );
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    });
  };

  const uploaded = attachment.serverItemId !== undefined;
  return (
    <div className="attach-chip">
      <button
        type="button"
        className="attach-open"
        title={
          attachment.missingBlob
            ? undefined
            : attachment.kind === 'photo'
              ? 'Click to preview full screen'
              : 'Click to open the PDF'
        }
        disabled={attachment.missingBlob}
        onClick={openPreview}
      >
        {thumbUrl ? (
          <img src={thumbUrl} alt="Attached photo" className="attach-thumb" />
        ) : (
          <span className="attach-icon" aria-hidden>
            {attachment.kind === 'pdf' ? '📄' : '📷'}
          </span>
        )}
        <span className="attach-meta">
          {attachment.size > 0
            ? `${Math.max(1, Math.round(attachment.size / 1024))} KB`
            : attachment.kind === 'pdf'
              ? 'PDF'
              : 'Photo'}
          {attachment.rejected ? (
            <span className="error" title={attachment.rejected}> · not accepted — {attachment.rejected}</span>
          ) : attachment.missingBlob ? (
            <span className="error"> · lost locally — re-attach</span>
          ) : uploaded ? (
            <span className="muted"> · ✓ uploaded</span>
          ) : (
            <span className="muted"> · uploads when synced</span>
          )}
        </span>
      </button>
      {!uploaded && (
        <ActionButton
          className="attach-remove"
          title="Remove this attachment"
          aria-label="Remove this attachment"
          busyLabel="…"
          onAction={onRemove}
        >
          ✕
        </ActionButton>
      )}
      {previewOpen && thumbUrl && (
        <ImageLightbox
          src={thumbUrl}
          alt="Attached photo (full size)"
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * Local bytes first; files that came over from another device (resume)
 * have no local copy, so fetch them from SharePoint (online only).
 */
async function attachmentBytes(attachment: LocalAttachment): Promise<Blob | undefined> {
  const local = await sessionStore.getAttachmentBlob(attachment.id);
  if (local || attachment.serverItemId === undefined) return local;
  return downloadAuditAttachment(attachment.serverItemId).catch(() => undefined);
}

function NoteEditor({
  onSave,
  onCancel,
  error,
}: {
  onSave: (text: string) => Promise<void>;
  onCancel: () => void;
  /** Why the last save failed — shown here, where the user is looking. */
  error?: string | null;
}) {
  const [text, setText] = useState('');
  const lock = useActionLock();
  return (
    <div className="note-editor">
      <textarea
        className="q-input"
        rows={2}
        autoFocus
        maxLength={10_000}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Note…"
      />
      {error && (
        <p className="error" role="alert" style={{ margin: '0.25rem 0' }}>
          {error}
        </p>
      )}
      <div className="q-extras-buttons">
        <ActionButton
          className="btn btn-small btn-primary"
          lock={lock}
          disabled={!text.trim()}
          onAction={() => onSave(text.trim())}
        >
          Save note
        </ActionButton>
        <button
          type="button"
          className="btn btn-small"
          disabled={lock.busy}
          onClick={(e) => {
            armTapShield(e); // the editor closes; the answer under it stays put
            onCancel();
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

function TaskSheet({
  contextText,
  storeLabel,
  templateName,
  defaultAssignee,
  onSave,
  onCancel,
  error,
}: {
  contextText: string;
  storeLabel: string;
  templateName: string;
  defaultAssignee: string;
  onSave: (task: {
    title: string;
    description: string;
    assignedTo: string;
    dueDate: string;
    proofRequired?: boolean;
  }) => Promise<void>;
  onCancel: () => void;
  /** Why the last save failed. */
  error?: string | null;
}) {
  const lock = useActionLock();
  // Starts blank (owner ruling 2026-10-21): the question reads like an
  // instruction ("Review 5 past due accounts…"), not the fix. It is shown
  // above as context and kept in the description.
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState(
    `From ${templateName} at ${storeLabel}: ${contextText}`,
  );
  const [assignedTo, setAssignedTo] = useState(defaultAssignee);
  const [dueDate, setDueDate] = useState(() => isoDaysAgo(-30));
  // Off by default (owner ruling 2026-10-25, B1).
  const [proofRequired, setProofRequired] = useState(false);

  // Company addresses only (G2) — checked here too, so it works offline and
  // a task never gets stuck unsyncable.
  const outsideAssignee = assignedTo.trim() !== '' && !isInternalEmail(assignedTo);
  // Today (Eastern) is the earliest due date — the picker's min doesn't stop typing (final-build rule, 2026-10-26).
  const dueOk = /^\d{4}-\d{2}-\d{2}$/.test(dueDate) && dueDate >= easternIsoDay(new Date());
  const valid = title.trim() !== '' && assignedTo.trim() !== '' && !outsideAssignee && dueOk;

  return (
    <Modal onClose={onCancel} busy={lock.busy} dirty={title.trim() !== ''}>
        <h2>New task</h2>
        <p className="task-sheet-context">
          <span className="muted">From the question</span> {contextText}
        </p>
        <div className="form-grid">
          <label className="span-2">
            Title
            {/* Two lines, so a long title can be read before saving (UI review U177) — still one line of text:
                Enter never adds a line break. */}
            <textarea
              className="q-input task-title-input"
              rows={2}
              value={title}
              onChange={(e) => setTitle(e.target.value.replace(/[\r\n]+/g, ' '))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.preventDefault();
              }}
              maxLength={255}
              placeholder="What needs to be fixed?"
              required
            />
          </label>
          <label className="span-2">
            Description
            <textarea
              className="q-input"
              rows={3}
              maxLength={10_000}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
          <PrincipalField
            type="user"
            label="Assign to"
            value={assignedTo}
            onChange={({ value }) => setAssignedTo(value)}
            required
          />
          {outsideAssignee && (
            <p className="error span-2" style={{ margin: 0, fontSize: '0.85rem' }}>
              Pick a person from the list — tasks go to {INTERNAL_DOMAINS_LABEL} addresses only.
            </p>
          )}
          <label>
            Due date
            <input
              type="date"
              min={easternIsoDay(new Date())}
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              required
              aria-invalid={dueDate !== '' && !dueOk ? true : undefined}
            />
            {dueDate !== '' && !dueOk && <span className="error" style={{ fontSize: '0.85rem' }}>Pick today or a later day.</span>}
          </label>
          <label className="checkbox span-2">
            <input type="checkbox" checked={proofRequired} onChange={(e) => setProofRequired(e.target.checked)} />
            Proof required (photo or PDF to complete)
          </label>
        </div>
        <p className="muted" style={{ fontSize: '0.85rem' }}>
          The task is created, and the assignee emailed, when this audit
          syncs.
        </p>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <div className="modal-actions">
          <button
            type="button"
            className="btn"
            disabled={lock.busy}
            onClick={(e) => {
              armTapShield(e);
              onCancel();
            }}
          >
            Cancel
          </button>
          <ActionButton
            className="btn btn-primary"
            lock={lock}
            busyLabel="Creating…"
            disabled={!valid}
            onAction={() =>
              onSave({
                title: title.trim(),
                description: description.trim(),
                assignedTo: assignedTo.trim(),
                dueDate,
                ...(proofRequired ? { proofRequired: true } : {}),
              })
            }
          >
            Create task
          </ActionButton>
        </div>
    </Modal>
  );
}

/** What the sync engine changes on a saved audit — the page re-renders only when this does. */
function syncSignature(s: LocalAuditSession): string {
  return JSON.stringify([
    s.status,
    s.serverId,
    s.headerDirty,
    s.dirtyQuestions.length,
    (s.notes ?? []).map((n) => [n.id, n.serverId, n.rejected]),
    (s.taskDrafts ?? []).map((t) => [t.id, t.serverId, t.rejected]),
    (s.attachments ?? []).map((a) => [a.id, a.serverItemId, a.missingBlob, a.rejected]),
  ]);
}
